import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, Droplets, Flame, Zap, Wrench, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth, usePrefs } from "@/lib/context";
import { fetcher, useMyProperties } from "@/lib/hooks";
import { PageHeader, Money, Empty } from "@/components/common";
import { PropertySelect, CurrencySelect } from "@/components/FormBits";
import { PhotoUpload } from "@/components/PhotoUpload";
import { Field } from "@/components/PropertyDialog";
import { tw } from "@/lib/i18n_extra";
import { DateInput } from "@/components/DateInput";

const TYPES = { assurance: ["Assurance", "Insurance", ShieldCheck], eau: ["Eau", "Water", Droplets], gaz: ["Gaz", "Gas", Flame], electricite: ["Électricité", "Electricity", Zap], entretien: ["Entretien", "Maintenance", Wrench], autre: ["Autre", "Other", FileText] };
const PERIODS = { mensuel: ["Mensuel", "Monthly"], trimestriel: ["Trimestriel", "Quarterly"], annuel: ["Annuel", "Yearly"] };
const EMPTY = { property_id: "", type: "assurance", supplier: "", number: "", start_date: "", end_date: "", amount: "", currency: "EUR", periodicity: "annuel", files: [], notes: "" };

function ContractDialog() {
  const { t, lang } = usePrefs();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    try { await api.post("/contracts", { ...f, amount: Number(f.amount || 0) }); qc.invalidateQueries({ queryKey: ["contracts"] }); setOpen(false); setF(EMPTY); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-sm bg-[#0F172A] hover:bg-[#B8860B]" data-testid="new-contract-button"><Plus size={16} className="mr-1" />{t("newContract")}</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto rounded-sm" data-testid="contract-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{t("newContract")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("property")}><PropertySelect value={f.property_id} onChange={set("property_id")} testId="contract-property-select" /></Field>
          <Field label={t("type")}>
            <Select value={f.type} onValueChange={set("type")}><SelectTrigger data-testid="contract-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{tw(v, lang)}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label={t("supplier")}><Input value={f.supplier} onChange={set("supplier")} data-testid="contract-supplier-input" /></Field>
          <Field label={t("number")}><Input value={f.number} onChange={set("number")} data-testid="contract-number-input" /></Field>
          <Field label={t("start")}><DateInput value={f.start_date} onChange={set("start_date")} data-testid="contract-start-input" /></Field>
          <Field label={t("end")}><DateInput value={f.end_date} onChange={set("end_date")} data-testid="contract-end-input" /></Field>
          <Field label={t("amount")}><Input type="number" value={f.amount} onChange={set("amount")} data-testid="contract-amount-input" /></Field>
          <Field label={t("currency")}><CurrencySelect value={f.currency} onChange={set("currency")} testId="contract-currency-select" /></Field>
          <Field label={t("periodicity")}>
            <Select value={f.periodicity} onValueChange={set("periodicity")}><SelectTrigger data-testid="contract-periodicity-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(PERIODS).map(([k, v]) => <SelectItem key={k} value={k}>{tw(v, lang)}</SelectItem>)}</SelectContent></Select>
          </Field>
        </div>
        <Field label={t("files")}><PhotoUpload value={f.files} onChange={set("files")} testId="contract-file-upload" /></Field>
        <Field label={t("notes")}><Textarea value={f.notes} onChange={set("notes")} data-testid="contract-notes-input" /></Field>
        <Button onClick={save} disabled={!f.property_id || !f.supplier || !f.start_date || !f.end_date} className="rounded-sm bg-[#0F172A]" data-testid="contract-save-button">{t("create")}</Button>
      </DialogContent>
    </Dialog>
  );
}

function DaysLeft({ d }) {
  const { t } = usePrefs();
  if (d == null) return null;
  const tone = d < 0 ? "bg-slate-200 text-slate-600" : d <= 60 ? "bg-rose-50 text-rose-700 border border-rose-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200";
  return <span className={`rounded-sm px-2 py-0.5 font-data text-[11px] ${tone}`}>{d < 0 ? t("expired") : `${d} ${t("daysLeft")}`}</span>;
}

export default function Contracts() {
  const { user } = useAuth();
  const { t, lang, fdate } = usePrefs();
  const qc = useQueryClient();
  const staff = ["admin", "supervision", "gestionnaire"].includes(user.role);
  const names = Object.fromEntries(useMyProperties().map((p) => [p.id, p.name]));
  const { data = [] } = useQuery({ queryKey: ["contracts"], queryFn: () => fetcher("/contracts") });
  const del = async (id) => { try { await api.delete(`/contracts/${id}`); qc.invalidateQueries({ queryKey: ["contracts"] }); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="contracts-page">
      <PageHeader eyebrow={t("secAsset")} title={t("contracts")}>{staff && <ContractDialog />}</PageHeader>
      {data.length === 0 && <Empty />}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {data.map((c) => {
          const [fr, en, Icon] = TYPES[c.type] || TYPES.autre;
          return (
            <div key={c.id} className="card-lift rounded-sm border border-slate-200 bg-white p-5" data-testid={`contract-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2 text-[#996515]"><Icon size={16} /><span className="eyebrow">{tw([fr, en], lang)}</span></div>
                <DaysLeft d={c.days_left} />
              </div>
              <h3 className="mt-2 font-display text-xl font-bold">{c.supplier}</h3>
              <div className="text-xs text-slate-500">{names[c.property_id]} · <span className="font-data">{c.number}</span></div>
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className="text-xs text-slate-500">{fdate(c.start_date)} → {fdate(c.end_date)}</span>
                <span><Money amount={c.amount} currency={c.currency} showContract={false} /> <span className="text-xs text-slate-400">/ {tw(PERIODS[c.periodicity], lang)}</span></span>
              </div>
              {c.notes && <p className="mt-2 text-xs text-slate-500">{c.notes}</p>}
              <div className="mt-3 flex items-center justify-between">
                <div className="flex gap-2">{c.files.map((p) => <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer" className="text-xs text-[#996515] underline">{p.split(".").pop().toUpperCase()}</a>)}</div>
                {staff && <button onClick={() => del(c.id)} className="text-slate-500 hover:text-rose-600" data-testid={`contract-delete-${c.id}`}><Trash2 size={14} /></button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
